// ---------------------------------------------------------------------------
// MASTER COMMERCIAL TERMS  - single source of truth (audit C2, M5)
// ---------------------------------------------------------------------------
// The UK/EU market audit found MOQ, lead times and sample terms contradicting
// each other in four different places (FAQ, Products page, Shipping Policy,
// product detail pages). A trade buyer reads all of them; inconsistency reads as
// carelessness or evasiveness.
//
// EVERY surface that states a commercial term must import it from here:
//   - MOQSection (Products page table + tiers)
//   - SpecSheets (per-line trade data)
//   - SupplyingTheUK page
//   - FAQ seed answers
//   - Product detail pages
//   - Legal / Shipping Policy copy
//
// Rule for maintainers: change the number HERE, never in a component. If a term
// is not yet confirmed by the business, leave it out rather than inventing one.
// ---------------------------------------------------------------------------

/** Per-product-line minimums, carton packing, loadability and lead times. */
export interface ProductLineTerms {
  /** Matches the SpecSheets `id` so the two never drift. */
  id: string;
  product: string;
  line: 'Bagasse' | 'Areca';
  /** Minimum order quantity, in units. */
  moqUnits: number;
  /** Units per export carton. `null` = built to specification. */
  unitsPerCarton: number | null;
  /** Outer carton dimensions in millimetres (metric-first, audit M2). */
  cartonMm: string | null;
  /** Indicative cartons per 40ft high-cube container. `null` = quoted per spec. */
  cartonsPer40HC: number | null;
  /** Production lead time in weeks, from confirmed order. */
  leadWeeks: [number, number];
  /** Customs tariff heading. Confirm the full 8-digit code with your broker. */
  hsCode: string;
}

/**
 * HS headings used across the range. Bagasse moulded tableware falls under
 * 4823.70 (moulded articles of paper pulp); areca palm leaf tableware is
 * classified under 4602.19 (basketwork/plaiting of vegetable material) by most
 * EU/UK brokers. The 8-digit suffix varies by destination  - always confirm with
 * the importer's customs agent before the commercial invoice is raised.
 */
export const HS_CODES = {
  bagasse: '4823.70',
  areca: '4602.19',
  cutlery: '3924.10',
} as const;

export const PRODUCT_LINE_TERMS: ProductLineTerms[] = [
  // ── Sugarcane bagasse line ────────────────────────────────────────────────
  {
    id: 'plates',
    product: 'Bagasse Plates',
    line: 'Bagasse',
    moqUnits: 1000,
    unitsPerCarton: 500,
    cartonMm: '600 × 400 × 450',
    cartonsPer40HC: 420,
    leadWeeks: [2, 3],
    hsCode: HS_CODES.bagasse,
  },
  {
    id: 'bowls',
    product: 'Bagasse Bowls',
    line: 'Bagasse',
    moqUnits: 1000,
    unitsPerCarton: 500,
    cartonMm: '600 × 400 × 500',
    cartonsPer40HC: 380,
    leadWeeks: [2, 3],
    hsCode: HS_CODES.bagasse,
  },
  {
    id: 'trays',
    product: 'Compartment Trays',
    line: 'Bagasse',
    moqUnits: 2000,
    unitsPerCarton: 400,
    cartonMm: '620 × 420 × 480',
    cartonsPer40HC: 400,
    leadWeeks: [3, 4],
    hsCode: HS_CODES.bagasse,
  },
  {
    id: 'cups',
    product: 'Cups & Lids',
    line: 'Bagasse',
    moqUnits: 2000,
    unitsPerCarton: 1000,
    cartonMm: '580 × 380 × 460',
    cartonsPer40HC: 450,
    leadWeeks: [3, 4],
    hsCode: HS_CODES.bagasse,
  },
  {
    id: 'cutlery',
    product: 'Cutlery Sets',
    line: 'Bagasse',
    moqUnits: 5000,
    unitsPerCarton: 2000,
    cartonMm: '500 × 350 × 350',
    cartonsPer40HC: 620,
    leadWeeks: [2, 4],
    hsCode: HS_CODES.cutlery,
  },
  {
    id: 'bagasse-custom',
    product: 'Bagasse Custom / Private Label',
    line: 'Bagasse',
    moqUnits: 10000,
    unitsPerCarton: null,
    cartonMm: null,
    cartonsPer40HC: null,
    leadWeeks: [5, 8],
    hsCode: HS_CODES.bagasse,
  },
  // ── Areca palm leaf line ──────────────────────────────────────────────────
  {
    id: 'areca-plates',
    product: 'Areca Plates',
    line: 'Areca',
    moqUnits: 2000,
    unitsPerCarton: 400,
    cartonMm: '600 × 400 × 450',
    cartonsPer40HC: 420,
    leadWeeks: [3, 5],
    hsCode: HS_CODES.areca,
  },
  {
    id: 'areca-bowls',
    product: 'Areca Bowls',
    line: 'Areca',
    moqUnits: 2000,
    unitsPerCarton: 400,
    cartonMm: '600 × 400 × 500',
    cartonsPer40HC: 380,
    leadWeeks: [3, 5],
    hsCode: HS_CODES.areca,
  },
  {
    id: 'areca-trays',
    product: 'Areca Platters & Trays',
    line: 'Areca',
    moqUnits: 3000,
    unitsPerCarton: 300,
    cartonMm: '700 × 500 × 450',
    cartonsPer40HC: 300,
    leadWeeks: [4, 6],
    hsCode: HS_CODES.areca,
  },
  {
    id: 'areca-custom',
    product: 'Areca Custom Shapes',
    line: 'Areca',
    moqUnits: 5000,
    unitsPerCarton: null,
    cartonMm: null,
    cartonsPer40HC: null,
    leadWeeks: [6, 10],
    hsCode: HS_CODES.areca,
  },
];

/** Look up a line's terms by id. Returns `undefined` for unknown ids. */
export const lineTerms = (id: string) => PRODUCT_LINE_TERMS.find((t) => t.id === id);

// ── Formatters (keep every surface phrasing terms identically) ──────────────

const nf = new Intl.NumberFormat('en-GB');

export const formatUnits = (units: number) => `${nf.format(units)} units`;
export const formatWeeks = ([min, max]: [number, number]) =>
  min === max ? `${min} weeks` : `${min}–${max} weeks`;
export const formatCarton = (t: ProductLineTerms) =>
  t.unitsPerCarton === null ? 'Per specification' : `${nf.format(t.unitsPerCarton)} / carton`;
export const formatLoadability = (t: ProductLineTerms) =>
  t.cartonsPer40HC === null
    ? 'Quoted per specification'
    : `~${nf.format(t.cartonsPer40HC)} cartons / 40ft HC (~${nf.format(
        t.cartonsPer40HC * (t.unitsPerCarton ?? 0)
      )} units)`;

// ── Terms that apply across the whole range ────────────────────────────────

/**
 * The overall MOQ range, derived from the table above so the headline figure can
 * never drift from the per-line rows.
 */
export const MOQ_RANGE = {
  min: Math.min(...PRODUCT_LINE_TERMS.map((t) => t.moqUnits)),
  max: Math.max(...PRODUCT_LINE_TERMS.map((t) => t.moqUnits)),
} as const;

/** The overall lead-time range, likewise derived. */
export const LEAD_TIME_RANGE: [number, number] = [
  Math.min(...PRODUCT_LINE_TERMS.map((t) => t.leadWeeks[0])),
  Math.max(...PRODUCT_LINE_TERMS.map((t) => t.leadWeeks[1])),
];

/** Headline strings for hero trust strips, meta descriptions and FAQ answers. */
export const HEADLINE_TERMS = {
  moq: `MOQ from ${formatUnits(MOQ_RANGE.min)}`,
  leadTime: `Production lead times ${formatWeeks(LEAD_TIME_RANGE)} from confirmed order`,
  privateLabelMoq: `Private label from ${formatUnits(10000)}`,
} as const;

/**
 * Sample policy  - ONE statement, used verbatim everywhere.
 * The audit found three contradictory versions of this on the live site.
 */
export const SAMPLE_POLICY = {
  summary: 'Sample packs are free; the buyer covers courier costs.',
  detail:
    'We send a free sample pack of up to 10 pieces per SKU (maximum 50 pieces per request) to registered businesses. The buyer covers courier charges, quoted before dispatch. Samples are picked from current stock and dispatched within 5 business days; allow 7–10 days in transit to the UK and EU.',
  maxPieces: 50,
  dispatchDays: 5,
} as const;

/** Shipping terms buyers filter on. */
export const TRADE_TERMS = {
  incoterms: ['EXW', 'FOB', 'CIF', 'CFR', 'DAP', 'DDP'],
  /** Incoterms 2020 is the revision our contracts reference. */
  incotermsRevision: 'Incoterms® 2020',
  payment: [
    '30% advance against proforma invoice, 70% against scanned bill of lading',
    'Irrevocable letter of credit at sight, confirmed by a UK or EU bank',
    'Open account terms considered after three completed shipments',
  ],
  currencies: ['GBP', 'EUR'],
  ukPorts: ['Felixstowe', 'London Gateway', 'Southampton'],
  euPorts: ['Rotterdam', 'Antwerp', 'Hamburg'],
  originPorts: ['Nhava Sheva (JNPT)', 'Chennai', 'Mundra'],
  /** Pallets: EU/UK phytosanitary requirement is ISPM 15 heat treatment. */
  palletStandard: 'ISPM 15 heat-treated (HT) pallets  - never methyl bromide fumigated',
} as const;

/** Volume pricing tiers. Percentages are indicative; final price is on quote. */
export const PRICING_TIERS = [
  {
    name: 'Starter',
    range: `${nf.format(1000)} – ${nf.format(9999)} units`,
    discount: 'List price',
    highlight: false,
    perks: ['Standard catalogue range', 'Stock colours & sizes', 'Sea or air freight'],
  },
  {
    name: 'Wholesale',
    range: `${nf.format(10000)} – ${nf.format(49999)} units`,
    discount: 'Up to 15% off',
    highlight: true,
    perks: ['Volume pricing', 'Priority production slot', 'Dedicated account manager'],
  },
  {
    name: 'Distributor',
    range: `${nf.format(50000)}+ units`,
    discount: 'Up to 30% off',
    highlight: false,
    perks: ['Best per-unit pricing', 'Scheduled repeat orders'], // 'Private-label & custom moulds',
  },
] as const;
